/**
 * Orders list: desktop table + mobile card list with date/search filters.
 * Phone: filters collapse on scroll so order cards win the viewport.
 */
import React, { useContext, useMemo, useState, useRef, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter } from 'react-table';
import { OrderContext } from '../App';
import { TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pickerTheme } from './pickerTheme';
import { STATUS_FILTERS, getOrderStatusGroup } from '../utils/orderStatusGroups';
import './OrderTable.css';

/** Both date fields share these. Phone dialog stays centered. */
const dateSlotProps = {
  textField: { size: 'small', className: 'date-selector' },
  toolbar: { hidden: true },
  openPickerIcon: { fontSize: 'small' },
};

/** True if value parses to a real instant (handles null/undefined from API). */
function isValidDate(d) {
  if (d == null || d === '') return false;
  const t = new Date(d).getTime();
  return !Number.isNaN(t);
}

/** Map order status code to badge modifier class. */
function statusBadgeClass(status) {
  if (!status) return 'status-badge';
  const s = String(status).toUpperCase();
  if (s === 'CANCL') return 'status-badge status-badge--cancelled';
  if (['DELVD', 'COMPLETE', 'COMPLETED', 'DEPCONS'].includes(s)) return 'status-badge status-badge--complete';
  if (['ASSGN', 'DISP', 'ENTRY', 'AVAIL'].includes(s)) return 'status-badge status-badge--assigned';
  if (['PICKD', 'ARRSHIP', 'DEPSHIP', 'SPOTTED', 'SPTLD', 'ARRCONS', 'UNLOADING'].includes(s)) {
    return 'status-badge status-badge--transit';
  }
  return 'status-badge status-badge--transit';
}

/** Selected row class only — no pastel status row fills. */
function rowStatusClass(isSelected) {
  return isSelected ? 'selected-row' : '';
}

/** Format pickup/delivery cell: date on top, time below. */
function formatDateCell(value) {
  if (!value) return '';
  const date = new Date(value);
  if (date.getFullYear() === 1969) return '';
  return (
    <div className="date-cell">
      <span>{date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}</span>
      <span className="time-sub">{date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
    </div>
  );
}

/** Short date string for mobile cards. */
function formatShortDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (date.getFullYear() === 1969) return '—';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
    + ' '
    + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function OrderTable({ onDateChange, onSearch, instance }) {
  const { orders, viewMap, viewShare, selectedStartDate, selectedEndDate, setSelectedStartDate, setSelectedEndDate, selectedOrder } = useContext(OrderContext);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const lastScrollTop = useRef(0);

  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    const startDateTime = new Date(selectedStartDate);
    startDateTime.setHours(0, 0, 0, 0);

    const endDateTime = new Date(selectedEndDate);
    endDateTime.setHours(23, 59, 59, 999);

    return orders.filter(order => {
      const pickupValid = isValidDate(order["Pickup Date"]);
      const deliveryValid = isValidDate(order["Delivery Date"]);

      if (!pickupValid && !deliveryValid) {
        if (!isValidDate(order["Deliver By End"])) return false;
        const deliverBy = new Date(order["Deliver By End"]);
        return deliverBy >= startDateTime && deliverBy <= endDateTime;
      }

      const pickupDate = new Date(order["Pickup Date"]);
      const deliveryDate = new Date(order["Delivery Date"]);

      return (
        (pickupDate >= startDateTime && pickupDate <= endDateTime) ||
        (deliveryDate >= startDateTime && deliveryDate <= endDateTime) ||
        (pickupDate <= startDateTime && deliveryDate >= startDateTime) ||
        (pickupDate <= endDateTime && deliveryDate >= endDateTime)
      );
    });
  }, [orders, selectedStartDate, selectedEndDate]);

  const columns = useMemo(() => [
    { Header: 'Order ID', accessor: 'order_id' },
    { Header: 'Sender', accessor: 'Shipper' },
    { Header: 'Receiver', accessor: 'Receiver' },
    { Header: 'Commodity', accessor: 'commodity' },
    {
      Header: 'Status',
      accessor: 'Status',
      Cell: ({ row }) => {
        const { Status: status, shipper_temp, consignee_temp, haul_temp_f_min, haul_temp_f_max } = row.original;

        const cRaw = consignee_temp != null ? String(consignee_temp).trim() : '';
        const sRaw = shipper_temp != null ? String(shipper_temp).trim() : '';
        const cVal = cRaw || sRaw;
        const cNum = cVal && !isNaN(Number(cVal));

        let tempClass = 'temp-green';
        if (cNum) {
          const v = Number(cVal);
          if (haul_temp_f_min != null && haul_temp_f_max != null) {
            if (v < Number(haul_temp_f_min)) tempClass = 'temp-red';
          } else {
            if (v < 300) tempClass = 'temp-red';
            else if (v <= 330) tempClass = 'temp-yellow';
          }
        }

        return (
          <div>
            <span className={statusBadgeClass(status)}>{status}</span>
            {cVal && (
              <div className={`temperature ${cNum ? tempClass : ''}`}>
                @{'\u00A0'}{cVal}{cNum ? '° F' : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Pickup',
      accessor: 'Pickup Date',
      sortType: (rowA, rowB) => {
        const a = new Date(rowA.values['Pickup Date']).getTime();
        const b = new Date(rowB.values['Pickup Date']).getTime();
        return a < b ? -1 : a > b ? 1 : 0;
      },
      Cell: ({ value }) => formatDateCell(value)
    },
    {
      Header: 'Delivery',
      accessor: 'Delivery Date',
      sortType: (rowA, rowB) => {
        const a = new Date(rowA.values['Delivery Date']).getTime();
        const b = new Date(rowB.values['Delivery Date']).getTime();
        return a < b ? -1 : a > b ? 1 : 0;
      },
      Cell: ({ value }) => formatDateCell(value)
    },
    {
      Header: 'Actions',
      Cell: ({ row }) => {
        const order = row.original;
        return (
          <div className="action-buttons">
            <Button
              variant="contained"
              size="small"
              className="map-button"
              onClick={() => viewMap(order)}
            >
              View Location
            </Button>
            <Tooltip title="Beta — future release" arrow>
              <span className="invoice-button-wrap">
                <Button
                  disabled
                  variant="contained"
                  size="small"
                  className="invoice-button"
                >
                  Download Documents
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" size="small" className="share-button" onClick={() => viewShare(order)}> Share </Button>
          </div>
        );
      }
    }
  ], [viewMap, viewShare]);

  const data = useMemo(() => filteredOrders, [filteredOrders]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      autoResetGlobalFilter: false,
      initialState: {
        sortBy: [
          {
            id: 'Delivery Date',
            desc: true
          }
        ]
      }
    },
    useGlobalFilter,
    useSortBy
  );

  const { globalFilter } = state;

  // react-table rows already reflect the date range, text search, and sorting.
  const statusCounts = STATUS_FILTERS.reduce((counts, filter) => {
    counts[filter.id] = 0;
    return counts;
  }, {});
  rows.forEach(row => {
    statusCounts.all += 1;
    statusCounts[getOrderStatusGroup(row.original.Status)] += 1;
  });
  const visibleRows = selectedStatus === 'all'
    ? rows
    : rows.filter(row => getOrderStatusGroup(row.original.Status) === selectedStatus);
  const emptyMessage = selectedStatus === 'all' && !globalFilter
    ? 'No orders for this date range'
    : 'No orders match these filters';

  /** Hide filters on scroll down; show again on scroll up (modern chrome). */
  const handleCardListScroll = useCallback((e) => {
    const top = e.currentTarget.scrollTop;
    const prev = lastScrollTop.current;
    const delta = top - prev;
    if (Math.abs(delta) < 10) return;
    if (delta > 0 && top > 16) {
      setFiltersOpen(false);
    } else if (delta < 0) {
      setFiltersOpen(true);
    }
    lastScrollTop.current = top;
  }, []);

  /** Action buttons shared by desktop cells and mobile cards. */
  const renderActions = (order) => (
    <>
      <Button
        variant="contained"
        size="small"
        className="map-button"
        onClick={() => viewMap(order)}
      >
        View Location
      </Button>
      <Tooltip title="Beta — future release" arrow>
        <span className="invoice-button-wrap">
          <Button
            disabled
            variant="contained"
            size="small"
            className="invoice-button"
          >
            Download Documents
          </Button>
        </span>
      </Tooltip>
      <Button variant="contained" size="small" className="share-button" onClick={() => viewShare(order)}>
        Share
      </Button>
    </>
  );

  return (
    <div className="order-table-container">
      <div className={`table-header${filtersOpen ? '' : ' table-header--filters-collapsed'}`}>
        <div className="table-header-top">
          <h2>Orders</h2>
        </div>
        <div className={`filters${filtersOpen ? '' : ' filters--collapsed'}`}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ThemeProvider theme={pickerTheme}>
              <div className="filters-dates">
                <DatePicker
                  label="Start Date"
                  value={selectedStartDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setSelectedStartDate(newValue);
                      onDateChange(newValue, selectedEndDate);
                    }
                  }}
                  slotProps={dateSlotProps}
                />
                <DatePicker
                  label="End Date"
                  value={selectedEndDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setSelectedEndDate(newValue);
                      onDateChange(selectedStartDate, newValue);
                    }
                  }}
                  slotProps={dateSlotProps}
                />
              </div>
            </ThemeProvider>
          </LocalizationProvider>
          <div className="filters-search-row">
            <TextField
              value={globalFilter || ''}
              onChange={e => setGlobalFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearch();
                }
              }}
              placeholder="Search orders..."
              className="search-input"
              size="small"
            />
            <Button variant="contained" onClick={onSearch} className="search-button">
              Search
            </Button>
          </div>
        </div>
        <div className={`status-quick-filters${filtersOpen ? '' : ' status-quick-filters--collapsed'}`} role="group" aria-label="Filter orders by status">
          {STATUS_FILTERS.filter(filter => filter.id !== 'other' || statusCounts.other > 0 || selectedStatus === 'other').map(filter => (
            <button
              key={filter.id}
              type="button"
              className={`status-filter-chip${selectedStatus === filter.id ? ' status-filter-chip--selected' : ''}`}
              data-status={filter.id}
              aria-pressed={selectedStatus === filter.id}
              onClick={() => setSelectedStatus(filter.id)}
            >
              <span>{filter.label}</span>
              <span className="status-filter-count">{statusCounts[filter.id]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="desktop-table-view">
        <TableContainer component={Paper} className="table-scroll-container">
          <Table {...getTableProps()} className="order-table">
            <TableHead>
              {headerGroups.map(headerGroup => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <TableCell
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={column.isSorted ? (column.isSortedDesc ? 'sort-desc' : 'sort-asc') : ''}
                    >
                      {column.render('Header')}
                      {column.isSorted && (
                        <i className={`fas fa-chevron-${column.isSortedDesc ? 'down' : 'up'} sort-icon`} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="orders-empty fade-in">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, i) => {
                  prepareRow(row);
                  const isSelected = selectedOrder && selectedOrder.order_id === row.original.order_id;
                  const rowProps = row.getRowProps();
                  return (
                    <TableRow
                      {...rowProps}
                      className={`fade-up ${rowStatusClass(isSelected)}`}
                      style={{ ...rowProps.style, '--i': Math.min(i, 12) }}
                    >
                      {row.cells.map(cell => (
                        <TableCell {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className="mobile-card-view" onScroll={handleCardListScroll}>
        {visibleRows.length === 0 ? (
          <p className="orders-empty fade-in">{emptyMessage}</p>
        ) : (
          visibleRows.map((row, i) => {
            prepareRow(row);
            const order = row.original;
            const isSelected = selectedOrder && selectedOrder.order_id === order.order_id;
            const cardClass = [
              'order-card',
              'fade-up',
              isSelected ? 'order-card--selected' : '',
            ].filter(Boolean).join(' ');

            return (
              <div className={cardClass} key={order.order_id} style={{ '--i': Math.min(i, 12) }}>
                <div className="card-header">
                  <span className="order-id">{order.order_id}</span>
                  <span className={statusBadgeClass(order.Status)}>{order.Status}</span>
                </div>
                <div className="card-body">
                  <div className="info-row">
                    <span className="label detail-label">Sender</span>
                    <span className="value detail-value">{order.Shipper || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label detail-label">Receiver</span>
                    <span className="value detail-value">{order.Receiver || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label detail-label">Commodity</span>
                    <span className="value detail-value">{order.commodity || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label detail-label">Pickup</span>
                    <span className="value detail-value">{formatShortDate(order['Pickup Date'])}</span>
                  </div>
                  <div className="info-row">
                    <span className="label detail-label">Delivery</span>
                    <span className="value detail-value">{formatShortDate(order['Delivery Date'])}</span>
                  </div>
                </div>
                <div className="card-actions">
                  {renderActions(order)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default OrderTable;
